import { createContext, PropsWithChildren, useContext } from 'react';

import { CampaignCategoryDto } from 'services/campaign/campaignService.dto';
import { ItemCategoryDto, ItemConditionDto, PriceRangeDto } from 'services/item/itemService.dto';
import { ChannelDto } from 'services/notification/notificationService.dto';
import { OrderConfigDto } from 'services/order/orderService.dto';
import { PaymentConfigDto } from 'services/payment/paymentService.dto';
import { ReasonDto } from 'services/report/reportService.dto';
import { CurrentUserDto } from 'services/user/userService.dto';

import { CacheState } from './cachedDataTypes';
import { useCacheState } from './useCacheState';

export interface CacheContextType {
  campaignCategories: CacheState<CampaignCategoryDto[]>;
  currentUser: CacheState<CurrentUserDto>;
  itemCategories: CacheState<ItemCategoryDto[]>;
  itemConditions: CacheState<ItemConditionDto[]>;
  paymentConfig: CacheState<PaymentConfigDto>;
  orderConfig: CacheState<OrderConfigDto>;
  priceRange: CacheState<PriceRangeDto>;
  reportReasons: CacheState<ReasonDto[]>;
  notificationChannels: CacheState<ChannelDto[]>;
}

const CacheContext = createContext<CacheContextType>(null!);

function CacheProvider({ children }: PropsWithChildren) {
  const campaignCategories = useCacheState<CampaignCategoryDto[]>();
  const currentUser = useCacheState<CurrentUserDto>();
  const itemCategories = useCacheState<ItemCategoryDto[]>();
  const itemConditions = useCacheState<ItemConditionDto[]>();
  const paymentConfig = useCacheState<PaymentConfigDto>();
  const orderConfig = useCacheState<OrderConfigDto>();
  const priceRange = useCacheState<PriceRangeDto>();
  const reportReasons = useCacheState<ReasonDto[]>();
  const notificationChannels = useCacheState<ChannelDto[]>();

  return (
    <CacheContext.Provider
      value={{
        campaignCategories,
        currentUser,
        itemCategories,
        itemConditions,
        paymentConfig,
        orderConfig,
        priceRange,
        reportReasons,
        notificationChannels,
      }}>
      {children}
    </CacheContext.Provider>
  );
}

const useCache = () => useContext(CacheContext);

export { CacheProvider, useCache };
